import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UilUser from '@iconscout/react-unicons/icons/uil-user';
import UilKeySkeleton from '@iconscout/react-unicons/icons/uil-key-skeleton';
import { UilUpload } from '@iconscout/react-unicons';
import { theme } from 'helpers/variables';
import { setLanguage, stopPropogation } from 'features/general/generalSlice';
import i18n from 'app/i18n';
import ChangePassword from 'pages/portal/modal/ChangePassword';
import SimpleSelectBox from 'helpers/ui-controls/ui-components/SimpleSelectBox';
import UserModal from 'pages/portal/modal/UserModal';
import storage from 'helpers/storage';
import { getUserAsync, selectUser } from 'features/users/userSlice';
import { useTranslation } from 'react-i18next';
import RenderSidebarTooltip from 'helpers/ui-controls/renderSidebarTooltip';
import { globalLogout } from 'helpers/generic-logout-method';
import { changeThemeColor } from './helper';
import config from 'config';
import alterImage from 'static/images/alter_image.webp';
import TruncateWithToolTip from 'components/TruncateWithToolTip';

const ProfileNav = ({ showSideBar, onLogOutRemoveChat }) => {
  const user = JSON.parse(storage.getItem('Profile'));
  const [menuShow, setMenuShow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const profile = useSelector(selectUser).user;
  const [selectedTheme, setSelectedTheme] = useState('gray');
  const { t } = useTranslation();
  const [userImageUrl, setUserImageUrl] = useState(user.ImageUrl);
  const dispatch = useDispatch();
  const languageOptions = [
    { value: 'en-US', text: 'English' },
    { value: 'sp-SP', text: 'Spanish' },
    { value: 'fr-FR', text: 'French' }
  ];

  const themeOptions = [
    { value: 'blue', text: 'OnRoll' },
    { value: 'gray', text: 'Apexchat' },
    { value: 'dark-grey', text: 'Dark Grey' },
    { value: 'turq-green', text: 'Turquoise Green' },
    { value: 'eco-green', text: 'Eco Green' },
    { value: 'orange', text: 'Orange' }
  ];

  const allowedEnvironmentForThemeSelectionDropdown = ['production', 'beta'];
  const localStorageLanguage = JSON.parse(storage.getItem('language'));
  const logoutHandler = () => {
    onLogOutRemoveChat();
    globalLogout();
  };

  const useCloseOnClickedOutside = async ref => {
    await dispatch(stopPropogation(ref)).then(res => {
      if (res) setMenuShow(false);
    });
  };

  // use ref on wrapper element
  const wrapperRef = useRef(null);
  // calling the method to check if the clicked event
  // happened inside / outside the wrapper
  // and pass the wrapper ref
  useCloseOnClickedOutside(wrapperRef);

  const toggleDropdownHandler = e => {
    let show = !menuShow;
    setMenuShow(show);
  };
  const languageHandler = (el, field, language) => {
    let tempLanguage;
    if (language) {
      tempLanguage = language;
      setMenuShow(false);
    } else {
      tempLanguage = localStorageLanguage;
    }
    i18n.changeLanguage(tempLanguage);
    dispatch(setLanguage(tempLanguage));
  };

  const openModal = () => {
    let showModalState = !showModal;
    setShowModal(showModalState);
    setMenuShow(false);
  };

  const closeModal = () => {
    let updatedUser = JSON.parse(storage.getItem('Profile'));
    setShowModal(false);
    if (updatedUser?.RequirePasswordReset) {
      logoutHandler();
    }
  };

  const openCloseProfileModal = () => {
    let showModalState = !showProfileModal;
    setShowProfileModal(showModalState);
    if (showModalState)
      dispatch(
        getUserAsync({
          params: { id: user.UserID }
        })
      );
    let userSavedInLS = JSON.parse(storage.getItem('Profile'));
    let updatedUser = { ...profile };
    userSavedInLS.ImageUrl = updatedUser.imageCloudSecureUrl;
    userSavedInLS.DisplayName = updatedUser.displayName;
    storage.setItem('Profile', userSavedInLS);
    setMenuShow(false);
  };

  const themeHandler = (el, field, theme) => {
    changeThemeColor(theme);
    setSelectedTheme(theme);
  };

  useEffect(() => {
    languageHandler();
    if (user?.RequirePasswordReset) {
      openModal();
    }
  }, [profile, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const onErrorImageUrl = e => {
    setUserImageUrl(alterImage);
  };

  return (
    <div className="profile-nav" ref={wrapperRef}>
      <div
        className="avatar-nav d-block"
        onClick={e => toggleDropdownHandler(e)}
      >
        {/* conditional toggle avtar/usericon */}
        <div className="userIcon d-flex align-items-center ">
          {user?.ImageUrl ? (
            <RenderSidebarTooltip
              showSidebar={showSideBar}
              title={user.DisplayName}
              icon={
                <img
                  src={userImageUrl}
                  className="avatar-styles"
                  alt="user"
                  onError={onErrorImageUrl}
                />
              }
            />
          ) : (
            <RenderSidebarTooltip
              showSidebar={showSideBar}
              title={user?.DisplayName}
              icon={
                <img src={alterImage} className="avatar-styles" alt="user" />
              }
              // icon={<UilUser size="16" color="white" className="avatar-icon" />}
            />
          )}
          {showSideBar ? (
            <span className="userText fw-600">
              {user?.DisplayName ? user.DisplayName : ''}
            </span>
          ) : null}
        </div>
      </div>
      {/* toggle the menu on condition check */}
      {menuShow && (
        <div
          className={`sidebar-bottom-menu${showSideBar ? '-open' : ''} `}
          style={{ width: '15rem', top: '52px' }}
        >
          <ul className="dropdow-list">
            <li className="notification-list-item d-flex m-0 p-2 align-items-center">
              <RenderSidebarTooltip
                showSidebar={showSideBar}
                title={user.DisplayName}
                icon={
                  <img
                    src={userImageUrl || ''}
                    className="avatar-styles"
                    alt="user"
                    onError={onErrorImageUrl}
                  />
                }
              />
              <div className="ml-2">
                <div>{user?.DisplayName}</div>
                <TruncateWithToolTip
                  text={user?.Email}
                  tooltip={user?.Email.length > 17}
                  limit={17}
                />
                {/* <div></div> */}
              </div>
            </li>
            <li className="notification-list-item">
              <div className="select-title py-2">
                {t('company.general.portallang')}
              </div>
              <SimpleSelectBox
                dataProperty={localStorageLanguage || ''}
                options={languageOptions}
                handleChange={languageHandler}
                customStyle={{
                  fontSize: '0.8125rem',
                  color: 'var(--black)',
                  height: '33px'
                }}
              />
              {/* <select
                className="form-control select"
                value={language}
                onChange={e => languageHandler(e.target.value)}
              >
                <option value="en-US">English</option>
                <option value="sp-SP">Spanish</option>
                <option value="fr-FR">French</option>
              </select> */}
            </li>
            {!allowedEnvironmentForThemeSelectionDropdown.includes(
              config.environment
            ) && (
              <li className="notification-list-item">
                <div className="select-title py-2">Theme</div>
                <SimpleSelectBox
                  dataProperty={selectedTheme}
                  options={themeOptions}
                  handleChange={themeHandler}
                  customStyle={{
                    fontSize: '0.8125rem',
                    color: 'var(--black)',
                    height: '33px'
                  }}
                />
              </li>
            )}
            <li className="notification-list-item">
              <a
                href="#"
                style={{ color: 'var(--primary)' }}
                onClick={openCloseProfileModal}
              >
                <UilUser
                  size="16"
                  color={'var(--primary)'}
                  className="list-icon"
                />
                {t('company.general.profile')}
              </a>
            </li>
            <li className="notification-list-item">
              <a
                href="#"
                style={{ color: 'var(--primary)' }}
                onClick={openModal}
              >
                <UilKeySkeleton
                  size="16"
                  color={'var(--primary)'}
                  className="list-icon"
                />
                {t('company.general.changePass')}
              </a>
            </li>
            {/* <li className="notification-list-item">
              <a
                href="#"
                style={{ color: theme.danger }}
                onClick={redirectHandler}
              >
                <UilSignout
                  size="16"
                  color={theme.danger}
                  className="list-icon"
                />
                Redirect to Old Portal
              </a>
            </li> */}
            <li className="notification-list-item">
              <a
                style={{ color: theme.danger, cursor: 'pointer' }}
                onClick={logoutHandler}
              >
                <UilUpload
                  size="16"
                  color={theme.danger}
                  className="list-icon"
                  style={{ transform: 'rotate(90deg)' }}
                />
                {t('company.general.logout')}
              </a>
            </li>
          </ul>
        </div>
      )}
      <UserModal
        title={'User Information'}
        eventHideModal={openCloseProfileModal}
        showModal={showProfileModal}
        editData={profile}
      />
      <ChangePassword
        showModal={showModal}
        eventHideModal={closeModal}
        requirePasswordReset={user && user.RequirePasswordReset}
      />
    </div>
  );
};

export default ProfileNav;
